import { useState, useEffect, useMemo, useCallback } from 'react';
import { stockyApi } from '../components/misc/StockyApi';

export const useItems = (authUser, initialFilters, initialPageSize = 20) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);

  const queryKey = useMemo(
    () =>
      JSON.stringify({
        page,
        pageSize,
        ...filters,
      }),
    [page, pageSize, filters],
  );

  const fetchItems = useCallback(async (options = {}) => {
    try {
      if (!authUser) {
        setItems([]);
        setTotal(0);
        setError(null);
        return [];
      }
      setError(null);
      const requestedPage = Number(options?.page ?? page) || 1;
      const requestedPageSize = Number(options?.pageSize ?? pageSize) || initialPageSize;
      const response = await stockyApi.getItems(authUser, {
        ...filters,
        page: requestedPage,
        pageSize: requestedPageSize,
      });
      const payload = response.data;
      if (payload && Array.isArray(payload.data)) {
        const nextItems = payload.data || [];
        setItems(nextItems);
        setTotal(Number(payload.total || 0));
        return nextItems;
      } else {
        const all = Array.isArray(payload) ? payload : [];
        setItems(all);
        setTotal(all.length);
        return all;
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
      setTotal(0);
      setError(error);
      return [];
    }
  }, [authUser, filters, page, pageSize, initialPageSize]);

  useEffect(() => {
    setIsLoading(true);
    fetchItems().finally(() => setIsLoading(false));
  }, [fetchItems, queryKey]);

  return {
    items,
    setItems,
    isLoading,
    setIsLoading,
    error,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    fetchItems,
  };
};
